import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { studentRecitationCerificateType } from '../../constants/types/student-type';
import IconAward from '../common/icon/IconAward';
import { openModal } from '../../store/features/modalSlice';
import { useAppDispatch } from '../../store/hook';
import TextWinds from '../common/TextWinds';
import ModalRecitationCertificate from '../modal/ModalRecitationCertificate';
import dayjs from 'dayjs';
import {
  getRecitationCertificates,
  getRecitation,
  postRecitationCertificates,
} from '../../api/utils/api/recitationApi';
import {
  RecitationListType,
  RecitationLevelType,
  RecitationType,
} from '../../constants/types/recitation-type';
import {
  CookieTypeUserInfo,
  CookieKey,
  CookieTypeJebsAiRecitation,
  createCookie,
} from '../../util/cookie';
import openPopupWindow from '../../util/popupWindow';
import { useParams } from 'react-router-dom';
import { LoadingSpinner } from '../Loading/LoadingSpinner';

interface RecitationResultProps {
  isTeacherAccount?: boolean;
  studentUid: string;
}
const RecitationResult = ({
  isTeacherAccount = false,
  studentUid,
}: RecitationResultProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<RecitationListType>();
  const [certificateList, setCertificateList] = useState<
    studentRecitationCerificateType[]
  >([]);

  const dataLoad = async () => {
    const userid = studentUid;
    if (!userid) throw new Error('userid is null');
    const certificate = await getRecitationCertificates(userid);
    const recitation = await getRecitation(userid);
    return { certificate, recitation };
  };

  useEffect(() => {
    setIsLoading(true);

    dataLoad().then(({ certificate, recitation }) => {
      setData(recitation);
      setCertificateList(certificate);
      setIsLoading(false);
    });
  }, []);

  if (isLoading) {
    return <LoadingSpinner message="데이터 조회중" />;
  }

  return (
    <>
      {!data ||
      !data.recitationProgress ||
      data.recitationProgress.length === 0 ? (
        <div className="flex h-[280px] items-center justify-center rounded-xl border border-dashed border-gray4 bg-gray0 px-4 text-center">
          <TextWinds type="content_body2" color="gray6">
            조회된 과제 현황이 없습니다.
          </TextWinds>
        </div>
      ) : (
        <>
          {data?.recitationProgress?.map((level) => (
            <StudentCertificatioinLevel
              key={level.groupLevel}
              isTeacherAccount={isTeacherAccount}
              level={level}
              studentProfile={data.studentInfo}
              certificate={certificateList?.find(
                (certificate) =>
                  certificate.type === level.type &&
                  certificate.subType === level.subType,
              )}
            />
          ))}
        </>
      )}
    </>
  );
};

/**
 * 하나의 레벨. 인증서 관리.
 * @param level 레벨 정보
 * @param certificate 인증서 정보. 없으면 인증서 발급 버튼 표시
 * @returns
 */
const StudentCertificatioinLevel = ({
  isTeacherAccount,
  level,
  studentProfile,
  certificate,
}: {
  isTeacherAccount: boolean;
  level: RecitationLevelType;
  studentProfile: {
    id: number;
    uid: string;
    name: string;
    englishName: string;
  };
  certificate?: studentRecitationCerificateType;
}) => {
  const { classId } = useParams();

  const dispatch = useAppDispatch();
  const [data, setData] =
    useState<studentRecitationCerificateType>(certificate);

  // 레벨 페스 확인
  const checkBadge = !level.results
    ?.map((book) => (book.passDate ? true : false))
    .includes(false);

  /**
   * 인증서 상태
   * none : 인증서 발급 불가 [ 레벨 페스 미달 ]
   * ready : 인증서 발급 가능
   * done : 인증서 발급 완료
   */
  const [state, setState] = useState<'none' | 'ready' | 'done'>(
    certificate ? 'done' : checkBadge ? 'ready' : 'none',
  );

  const openCertificateModal = (data: studentRecitationCerificateType) => {
    dispatch(
      openModal({
        body: (
          <ModalRecitationCertificate
            englishName={data.userEnglishName}
            subject={data.subType}
            date={data.createdAt}
          />
        ),
      }),
    );
  };

  // 인증서 발급 버튼 클릭
  const handleIssueCertificate = () => {
    postRecitationCertificates({
      uid: studentProfile.uid,
      name: studentProfile.name,
      englishName: studentProfile.englishName,
      type: level.type,
      subType: level.subType,
    }).then((data) => {
      setState('done');
      setData(data);
      openCertificateModal(data);
    });
  };

  const { setCookie } = createCookie<CookieTypeJebsAiRecitation>(
    CookieKey.JEBS_AI_RECITATION,
  );
  const { getCookie: getUserInfoCookie } = createCookie<CookieTypeUserInfo>(
    CookieKey.USER_INFO,
  );
  const { removeCookie: removeNewRecitationCookie } =
    createCookie<CookieTypeJebsAiRecitation>(CookieKey.JEBS_AI_RECITATION);

  const handleResultClick = (book: RecitationType) => {
    try {
      removeNewRecitationCookie();
    } catch (error) {
      // 쿠키가 없는 경우 무시
      // console.error(error);
    }

    const { id, uid } = getUserInfoCookie();

    setCookie(
      {
        student: {
          id: studentProfile.id,
          uid: studentProfile.uid,
        },
        teacher: {
          id: id,
          uid: uid,
          classId: classId,
        },
        todo: {
          id: book.todoId,
          type: 'JEBS_AI_RECITATION',
        },
        accessToken: localStorage.getItem('accessToken'),
      },
      1 / 24,
    );
    openPopupWindow(book.resultUrl, 1440, 1440 / 1080);
  };

  return (
    <div className="mt-4 flex flex-col items-center gap-3 first:mt-0 lg:flex-row">
      {/* <TextWinds type={'title_h3'} children={`Level ${level.groupLevel}`} /> */}
      <motion.img
        src={
          process.env.PUBLIC_URL +
          `/images/student/badge/recitation/LV${level.groupLevel}.png`
        }
        draggable={false}
        className={`h-[100px] ${checkBadge ? '' : 'grayscale filter'}`}
        whileTap={checkBadge ? { rotate: [0, 360] } : { x: [-0.5, 0.5] }}
        // whileInView={checkBadge ? { scale: 1.05 } : {}}
        transition={{
          repeat: Infinity,
          duration: 0.1,
          repeatType: 'mirror',
          ease: 'easeInOut',
        }}
      />
      <div className="w-full">
        <div className="flex items-center justify-between gap-2 lg:justify-start">
          <TextWinds type="title_h3">LEVEL {level.groupLevel}</TextWinds>
          {checkBadge && (
            <button
              onClick={
                state === 'done'
                  ? () => openCertificateModal(data)
                  : state === 'ready'
                    ? handleIssueCertificate
                    : undefined
              }
              className="rounded-lg border border-skyblue p-[2px] px-1 text-sm text-skyblue hover:bg-gray2"
            >
              {state === 'done' ? (
                <>
                  인증서 보기{' '}
                  <DocumentArrowDownIcon className="inline h-4 w-4" />
                </>
              ) : state === 'ready' ? (
                '인증서 발급'
              ) : (
                ''
              )}
            </button>
          )}
        </div>
        <div className="mt-2 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {level.results.map((book) => (
            <StudentCertificationBook
              key={book.title}
              isTeacherAccount={isTeacherAccount}
              book={book}
              onResultClick={handleResultClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const getBookResultText = (book: RecitationType) => {
  const scoreText = book.score !== null ? `${book.score}점` : '점수 없음';

  if (book.passDate) {
    return `합격 · ${dayjs(book.passDate).format('YYYY-MM-DD')} · ${scoreText}`;
  }

  if (book.failDate) {
    return `불합격 · ${dayjs(book.failDate).format('YYYY-MM-DD')} · ${scoreText}`;
  }

  if (book.testDate) {
    return `연습 · ${dayjs(book.testDate).format('YYYY-MM-DD')}`;
  }

  return '';
};

/**
 * 인증제 하나. book {n}
 * @param book 북 정보. 패스 여부
 * @returns
 */
const StudentCertificationBook = ({
  isTeacherAccount,
  book,
  onResultClick,
}: {
  isTeacherAccount: boolean;
  book: RecitationType;
  onResultClick: (book: RecitationType) => void;
}) => {
  const onoff = book.passDate ? true : false;
  // const onoff = true;

  const canViewResult =
    isTeacherAccount &&
    Boolean(book.passDate || book.failDate || book.testDate);

  return (
    <div className="flex flex-col items-center">
      <div
        className={`w-full rounded-lg py-3 text-center text-lg font-bold shadow-card ${
          onoff ? 'bg-spotLilac text-white' : 'bg-gray-100 text-gray-500'
        }`}
      >
        <div className="flex flex-row items-center justify-center gap-2">
          {onoff && <IconAward />}
          {book.title}
        </div>
      </div>

      <div className="mt-1 flex flex-col items-center gap-1">
        <TextWinds type="content_body3" color="gray7" className="text-center">
          {getBookResultText(book)}
        </TextWinds>

        {canViewResult && (
          <button
            type="button"
            className="rounded-lg border border-purple3 bg-white px-3 py-1 text-xs font-bold text-purple5 transition-colors hover:bg-purple1"
            onClick={() => onResultClick(book)}
          >
            결과보기
          </button>
        )}
      </div>
    </div>
  );
};

export default RecitationResult;
